// ============================================================
// ANSICHTSSACHE-AGENT – Ziehen und Ablegen
// ============================================================
// Die eine Regel, die ueberall gilt, wo man Position zieht:
// WAEHREND des Ziehens muss sichtbar sein, wo das Ding landen
// wird. Ohne diese Linie ist Ziehen ein Ratespiel — man laesst
// los und sieht erst danach, ob man richtig lag.
//
// Drei Teile:
//   `Ablegelinie`   die Marke, die zeigt, wohin es faellt
//   `useZiehen`     merkt sich, was gezogen wird und worueber
//   `haelfte`       obere oder untere Haelfte eines Elements
//
// Bewusst mit der HTML5-Schnittstelle des Browsers und ohne
// zusaetzliche Bibliothek: sie kann alles, was wir brauchen, und
// eine Abhaengigkeit, die man nicht hat, kann auch nicht
// veralten.
// ============================================================

"use client";

import { useRef, useState } from "react";

export type Position = "vor" | "nach";

/** Liegt der Zeiger in der oberen oder unteren Hälfte? */
export function haelfte(e: React.DragEvent<HTMLElement>): Position {
  const kasten = e.currentTarget.getBoundingClientRect();
  return e.clientY < kasten.top + kasten.height / 2 ? "vor" : "nach";
}

/**
 * Die Linie, die zeigt, wo es landet.
 *
 * Sie liegt ZWISCHEN den Elementen, nicht auf der Kante eines
 * Elements — mittig in der Luecke, mit Luft nach oben und unten.
 * Auf der Kante geklebt liest sie sich als Unterstreichung des
 * Elements darueber und nicht als Einfuegestelle.
 *
 * Sie liegt absolut und schiebt deshalb nichts weg: ein
 * Indikator, der Platz braucht, laesst die Liste unter dem
 * Zeiger springen — und dann zieht man auf ein Ziel, das sich
 * gerade wegbewegt hat.
 *
 * `luecke` ist der Abstand, den die Geschwister ohnehin
 * voneinander haben (`space-y-*`, `gap-*`). Die Linie setzt sich
 * in dessen Mitte. Ohne diese Angabe raet sie und liegt schief.
 *
 * `senkrecht` (seit 08.10.2026) fuer Dinge NEBENEINANDER — die
 * Plaettchen einer Schiene: „vor“ ist dann links, „nach“ rechts,
 * `einzug` der Abstand oben und unten, der Punkt sitzt oben.
 */
export function Ablegelinie({
  sichtbar,
  position,
  einzug = 0,
  luecke = 0,
  senkrecht = false,
}: {
  sichtbar: boolean;
  position: Position;
  /**
   * Abstand zum linken und rechten Rand, damit die Linie zur Zeile
   * passt — Pixel oder ein CSS-Wert wie `var(--sheet-body-edge)`.
   */
  einzug?: number | string;
  /** Abstand der Geschwister in Pixeln — die Linie zentriert sich darin. */
  luecke?: number;
  /** Fuer Dinge nebeneinander: die Linie steht, statt zu liegen. */
  senkrecht?: boolean;
}) {
  if (!sichtbar) return null;

  // Die Linie ist 2px stark; halb in die Luecke gerueckt liegt sie
  // genau auf deren Mitte.
  const versatz = -(luecke / 2) - 1;

  if (senkrecht) {
    return (
      <span
        aria-hidden
        style={{
          top: einzug,
          bottom: einzug,
          ...(position === "vor" ? { left: versatz } : { right: versatz }),
        }}
        className="bg-accent pointer-events-none absolute z-10 w-0.5 rounded-full"
      >
        <span className="bg-accent absolute -top-1 -left-[3px] h-2 w-2 rounded-full" />
      </span>
    );
  }

  return (
    <span
      aria-hidden
      style={{
        left: einzug,
        right: einzug,
        ...(position === "vor" ? { top: versatz } : { bottom: versatz }),
      }}
      className="bg-accent pointer-events-none absolute z-10 h-0.5 rounded-full"
    >
      {/* Der Punkt am Anfang macht aus der Linie eine Marke —
          eine nackte Linie liest sich als Trennstrich. */}
      <span className="bg-accent absolute -top-[3px] -left-1 h-2 w-2 rounded-full" />
    </span>
  );
}

export type Gezogen<T extends string> = {
  art: T;
  id: string;
  /** Woher es kam — bei Aufgaben die Liste. */
  ausId?: string;
};

/**
 * Merkt sich, was gerade gezogen wird und worüber es schwebt.
 *
 * Das Gezogene liegt in einem `ref`, weil die Ereignisbehandler
 * es sofort brauchen — ein Zustand waere dort einen Durchgang zu
 * spaet. Daneben steht dieselbe Kennung als Zustand, damit die
 * angefasste Zeile blass werden kann: das kostet genau zwei
 * Durchgaenge, einen beim Anfassen und einen beim Loslassen,
 * nicht einen je Mausbewegung.
 */
export function useZiehen<T extends string>() {
  const gezogen = useRef<Gezogen<T> | null>(null);
  const [aktivId, setAktivId] = useState<string | null>(null);
  const [ziel, setZiel] = useState<{ id: string; position: Position } | null>(null);

  function anfassen(was: Gezogen<T>) {
    gezogen.current = was;
    setAktivId(was.id);
  }

  function loslassen() {
    gezogen.current = null;
    setAktivId(null);
    setZiel(null);
  }

  function ueber(e: React.DragEvent<HTMLElement>, zielId: string) {
    if (!gezogen.current) return;
    e.preventDefault();
    e.stopPropagation();
    // Sich selbst markiert man nicht.
    if (gezogen.current.id === zielId) {
      setZiel(null);
      return;
    }
    const position = haelfte(e);
    setZiel((alt) =>
      alt?.id === zielId && alt.position === position ? alt : { id: zielId, position },
    );
  }

  /**
   * Ziel von Hand setzen — fuer Faelle, in denen die Haelfte des
   * Zeigers nicht entscheidet: das Ende einer Liste zum Beispiel
   * ist immer „dahinter“, egal wo man in der Flaeche schwebt.
   */
  function zielSetzen(id: string, position: Position) {
    setZiel((alt) => (alt?.id === id && alt.position === position ? alt : { id, position }));
  }

  /**
   * Kein Ziel — dort, wo Ablegen nichts aendern wuerde (vor oder
   * hinter dem Gezogenen selbst). Dann steht auch keine Linie da.
   */
  function zielLoeschen() {
    setZiel(null);
  }

  /** Zeigt dieses Ziel gerade die Linie, und auf welcher Seite? */
  function markiert(zielId: string): Position | null {
    return ziel?.id === zielId ? ziel.position : null;
  }

  return {
    /** Was gerade gezogen wird — null, wenn nichts. */
    gezogen,
    /** Kennung des Gezogenen als Zustand — für „wird gerade angefasst“. */
    aktivId,
    anfassen,
    loslassen,
    ueber,
    zielSetzen,
    zielLoeschen,
    markiert,
    /** Wo die Linie gerade steht — dort faellt es beim Loslassen. */
    ziel,
    /** Wird gerade überhaupt gezogen? */
    aktiv: () => gezogen.current !== null,
  };
}
